import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    TablePagination,
    CardContent,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    InputAdornment,
    TableFooter,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Chip,
    Divider,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Tabs,
    Tab,
    Paper,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Business as BusinessIcon,
    LocalHospital as HospitalIcon,
    CalendarToday as CalendarIcon,
    FilterList as FilterIcon,
    Sort as SortIcon,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    Person as PersonIcon,
    Description as DescriptionIcon,
    NoteAdd,
    Assignment as AssignmentIcon,
    Note,
    PictureAsPdf,
    Visibility,
    ArrowBackIos,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { format, parseISO, isValid } from 'date-fns';
import { clinicalModel } from '../Axios/AxiosConfig';

// Types for Lab Test Orders
interface LabOrderItem {
    testName: string;
    testCode: string;
    price: number;
    category: string;
}

interface LabOrder {
    id: string;
    orderId: string;
    patientName: string;
    patientEmail: string;
    patientPhone: string;
    doctorName?: string;
    companyId: string;
    companyName: string;
    orderType: 'Commercial' | 'Clinic';
    orderDate: string;
    collectionDate?: string;
    status: 'pending' | 'collected' | 'processing' | 'completed' | 'cancelled';
    totalAmount: number;
    items: LabOrderItem[];
    notes?: string;
    reportUrl?: string;
}

interface GroupedLabOrders {
    orderType: string;
    data: LabOrder[];
}

// Helper function to safely format dates
const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';

    try {
        // Handle different date formats
        let parsedDate: Date;

        if (dateString.includes('T') && (dateString.includes('Z') || dateString.includes('+'))) {
            // ISO format: 2025-05-09T11:30:57Z
            parsedDate = parseISO(dateString);
        } else if (dateString.includes('-') && dateString.split('-').length === 3) {
            // Date only format: 2025-05-09
            parsedDate = new Date(dateString + 'T00:00:00.000Z');
        } else if (dateString.includes('/') && dateString.includes(' ')) {
            // Custom format: 09/05/2025 17:00:57
            const [datePart, timePart] = dateString.split(' ');
            const [day, month, year] = datePart.split('/');
            const [hours, minutes, seconds] = timePart.split(':');
            parsedDate = new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`);
        } else {
            // Try to parse as-is
            parsedDate = new Date(dateString);
        }

        if (!isValid(parsedDate)) {
            return 'Invalid Date';
        }
        return format(parsedDate, 'dd/MM/yyyy HH:mm');
    } catch (error) {
        console.error('Error parsing date:', dateString, error);
        return 'Invalid Date';
    }
};

const LAB_REPORTS_API_URL = (process.env.REACT_APP_CLINICAL_URL as string | undefined) || 'https://api.profile.ayurai.in/getLabReportsByDateRange';

const parseDateForSorting = (dateString: string | null | undefined): number => {
    if (!dateString) return 0;

    try {
        let parsedDate: Date;

        if (dateString.includes('T') && (dateString.includes('Z') || dateString.includes('+'))) {
            // ISO format: 2025-05-09T11:30:57Z
            parsedDate = parseISO(dateString);
        } else if (dateString.includes('-') && dateString.split('-').length === 3) {
            // Date only format: 2025-05-09
            parsedDate = new Date(dateString + 'T00:00:00.000Z');
        } else if (dateString.includes('/') && dateString.includes(' ')) {
            // Custom format: 09/05/2025 17:00:57
            const [datePart, timePart] = dateString.split(' ');
            const [day, month, year] = datePart.split('/');
            const [hours, minutes, seconds] = timePart.split(':');
            parsedDate = new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`);
        } else {
            // Try to parse as-is
            parsedDate = new Date(dateString);
        }

        return isValid(parsedDate) ? parsedDate.getTime() : 0;
    } catch (error) {
        console.error('Error parsing date for sorting:', dateString, error);
        return 0;
    }
};

// Company options based on the API filter
const COMPANY_OPTIONS = [
    { id: 'all', name: 'All Companies' },
    { id: '1008', name: 'GUT Microbiome' },
    { id: '1009', name: 'Ayurai CGM' },
    { id: '1010', name: 'Chromosome labs' },
];



const getCompanyName = (companyId: string | undefined): string => {
    if (!companyId) return 'Unknown Company';
    const company = COMPANY_OPTIONS.find(c => c.id === companyId);
    return company ? company.name : `Company ${companyId}`;
};

const mapStatus = (apiStatus: string | undefined): 'pending' | 'collected' | 'processing' | 'completed' | 'cancelled' => {
    if (!apiStatus) return 'pending';

    const status = apiStatus.toLowerCase();
    switch (status) {
        case 'initiated':
        case 'order placed':
            return 'pending';
        case 'placed':
        case 'confirmed':
            return 'collected';
        case 'dispatched':
            return 'processing';
        case 'delivered':
            return 'completed';
        case 'cancelled':
            return 'cancelled';
        default:
            return 'pending';
    }
};

// Tab props for accessibility
const a11yProps = (index: number) => ({
    id: `lab-orders-tab-${index}`,
    'aria-controls': `lab-orders-tabpanel-${index}`,
});

// Memoized TabPanel component
const TabPanel = React.memo((props: { children?: React.ReactNode; index: number; value: number }) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`lab-orders-tabpanel-${index}`}
            aria-labelledby={`lab-orders-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
        </div>
    );
});

// Memoized DataGrid component to prevent unnecessary re-renders
const MemoizedDataGrid = React.memo(({
    rows,
    columns,
    loading,
    sortModel,
    onSortModelChange
}: {
    rows: LabOrder[];
    columns: GridColDef[];
    loading: boolean;
    sortModel: GridSortModel;
    onSortModelChange: (newSortModel: GridSortModel) => void;
}) => (
    <Box sx={{ width: '100%', height: 'calc(100vh - 300px)', minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
        rowHeight={60}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        pagination
        sx={{
          '& .MuiDataGrid-main': {
            width: '100%',
            overflow: 'hidden',
          },
          '& .MuiDataGrid-virtualScroller': {
            overflowX: 'hidden',
          },
            '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                padding: '0 16px',
            },
            '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid #e0e0e0',
            },
            '& .MuiDataGrid-row': {
                minHeight: '60px !important',
                '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.12)',
                    },
                },
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                outline: 'none',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                outline: 'none',
            },
        }}
        disableColumnMenu
        disableColumnFilter
        disableDensitySelector
        disableVirtualization={false}
        getRowHeight={() => 60}
        componentsProps={{
          pagination: {
            rowsPerPageOptions: [10, 25, 50],
            showFirstButton: true,
            showLastButton: true,
          },
        }}
      />
    </Box>
));

// Memoized Tab Content component
const MemoizedTabContent = React.memo(({
    group,
    sortedOrders,
    loading,
    sortModel,
    onSortModelChange,
    columns
}: {
    group: GroupedLabOrders;
    sortedOrders: LabOrder[];
    loading: boolean;
    sortModel: GridSortModel;
    onSortModelChange: (newSortModel: GridSortModel) => void;
    columns: GridColDef[];
}) => (
    <Box sx={{ height: 600, width: '100%', mt: 2 }}>
        <MemoizedDataGrid
            rows={sortedOrders}
            columns={columns}
            loading={loading}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
        />
    </Box>
));

const LabReportsPage: React.FC = React.memo(() => {
    const navigate = useNavigate();
    // State management
    const [startDate, setStartDate] = useState<Date | null>(new Date());
    const [endDate, setEndDate] = useState<Date | null>(new Date());
    const [companyFilter, setCompanyFilter] = useState<string>('all');
    // Debounced search state
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
    const [orders, setOrders] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [detailOpen, setDetailOpen] = useState<boolean>(false);
    const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
    const [tabValue, setTabValue] = useState(0);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: 'orderDate', sort: 'desc' }
    ]);

    // Handle tab change
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    // Get current tab's orders
    const currentTabOrders = useMemo(() => {
        if (!orders || orders.length === 0) return [];
        const currentTabData = orders[tabValue]?.data || [];
        return currentTabData;
    }, [orders, tabValue]);

    // Filter orders based on search query
    const searchFilteredOrders = useMemo(() => {
        if (!debouncedSearchQuery.trim()) return currentTabOrders;

        const query = debouncedSearchQuery.toLowerCase().trim();
        return currentTabOrders.filter((order : any) => {
            return (
                (order.patientName || '').toLowerCase().includes(query) ||
                (order.patientEmail || '').toLowerCase().includes(query) ||
                (order.patientPhone || '').toLowerCase().includes(query) ||
                (order.orderId || '').toLowerCase().includes(query) ||
                (order.companyName || '').toLowerCase().includes(query) ||
                (order.items && Array.isArray(order.items) && order.items.some((item : any) => (item.testName || '').toLowerCase().includes(query)))
            );
        });
    }, [currentTabOrders, debouncedSearchQuery]);

    // Sort orders based on sort model
    const sortedOrders = useMemo(() => {
        if (!sortModel || sortModel.length === 0) return searchFilteredOrders;

        const sorted = [...searchFilteredOrders].sort((a, b) => {
            for (const sortItem of sortModel) {
                const { field, sort } = sortItem;
                let aValue: any = a[field as keyof any];
                let bValue: any = b[field as keyof any];

                // Handle nested fields and type conversion
                if (field === 'orderDate') {
                    aValue = parseDateForSorting(a.orderDate);
                    bValue = parseDateForSorting(b.orderDate);
                } else if (field === 'totalAmount') {
                    aValue = Number(a.totalAmount);
                    bValue = Number(b.totalAmount);
                } else if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = (bValue as string).toLowerCase();
                }

                // Handle undefined values
                if (aValue == null && bValue == null) return 0;
                if (aValue == null) return sort === 'asc' ? -1 : 1;
                if (bValue == null) return sort === 'asc' ? 1 : -1;

                if (aValue < bValue) {
                    return sort === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sort === 'asc' ? 1 : -1;
                }
                return 0;
            }
            return 0;
        });

        return sorted;
    }, [searchFilteredOrders, sortModel?.length, sortModel?.[0]?.field, sortModel?.[0]?.sort]);

    // Fetch lab orders from API
    const fetchLabOrders = async () => {
        if (!startDate || !endDate) return;

        setLoading(true);
        setError(null);

        try {
            const body: any = {
                startDate: format(startDate, 'yyyy-MM-dd'),
                endDate: format(endDate, 'yyyy-MM-dd'),
                companyFilter: companyFilter === 'all' ? undefined : companyFilter,
            };

            const controller = new AbortController();

            const timeoutId = setTimeout(() => controller.abort(), 60000); // 1 minute timeout

            const res = await clinicalModel.post(`getLabReports`, body);

            clearTimeout(timeoutId);

            if (!res.status) throw new Error(`HTTP ${res.status}`);

           

            if (res.data.success && res.data.orders) {
                
                const transformedOrders: GroupedLabOrders[] =   res.data.orders.map((group: any) => ({
                    orderType: group.orderType,
                    data: group.data.map((order: any) => {
                       
                        const items = order.partners?.items?.map((item: any) => ({
                            testName: item.productName,
                            testCode: item.Sku,
                            price: item.Price,
                            category: 'Lab Test' 
                        })) || order.Data?.cartitems?.map((item: any) => ({
                            testName: item.name,
                            testCode: item.sku,
                            price: item.price,
                            category: item.company || 'Lab Test'
                        })) || [];

                        const customer = order.partners?.customer?.[0] || order.Data?.customerDetails?.shipping;
                        
                        const orderData = order.Data || {};

                        const { customerDetails } = orderData

                        const { shipping } = customerDetails

                        return {
                            id: order.id || order.UID,
                            orderId: orderData.Orderid || order.partners?.orderNumber,
                            patientName: shipping?.name || '',
                            patientEmail: shipping?.email || '',
                            patientPhone: shipping?.contact || '',
                            companyId: orderData.companyid || order.partners?.companyId,
                            companyName: getCompanyName(orderData.companyid || order.partners?.companyId),
                            orderType: group.orderType,
                            orderDate: order.partners?.orderDate || orderData.dateandtime,
                            status: mapStatus(orderData.OrderStatus || order.partners?.status),
                            totalAmount: orderData.total || orderData.subTotal || order.partners?.totalAmount || 0,
                            items: items,
                            notes: orderData.notes,
                            ...order,
                            reportUrl: order.Invoice?.Invoice || order.rawReport
                        };
                    })
                }));

                setOrders(transformedOrders);
                setSuccess(`Successfully loaded ${transformedOrders.reduce((total: number, group: GroupedLabOrders) => total + group.data.length, 0)} orders`);
                setTimeout(() => setSuccess(null), 3000);
            } else {
                throw new Error(res.data.message || 'Failed to fetch lab orders');
            }
        } catch (e: any) {
            console.error('Error fetching lab orders:', e);
            setError(e?.message || 'Failed to fetch lab orders');
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchLabOrders();
    }, [startDate, endDate, companyFilter]);

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Handle view order details
    const handleViewOrder = (order: LabOrder) => {
        setSelectedOrder(order);
        setDetailOpen(true);
    };

    // Handle generate report
    const handleGenerateReport = (order: LabOrder) => {
        navigate('/generate-report', { state: { order } });
    };

    // Handle sort model change
    const handleSortModelChange = (newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
    };

    // Memoized DataGrid columns configuration
    const columns = useMemo((): GridColDef[] => [
        {
            field: 'orderId',
            headerName: 'Order ID',
            flex: 1,
            sortable: true,
        },
        {
            field: 'patientName',
            headerName: 'Name',
            flex: 1.5,
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value} placement="top" arrow>
                    <Box 
                        sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            width: '100%',
                            cursor: 'pointer'
                        }}
                    >
                        {params.value}
                    </Box>
                </Tooltip>
            ),
        },
        // {
        //     field: 'patientPhone',
        //     headerName: 'Phone',
        //     flex: 1,
        //     sortable: true,
        // },
        {
            field: 'companyName',
            headerName: 'Company',
            flex: 1.2,
            sortable: true,
        },
        {
            field: 'orderDate',
            headerName: 'Order Date',
            flex: 1.2,
            sortable: true,
            valueFormatter: (params) => formatDate(params.value as string),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    color={
                        params.value === 'completed' ? 'success' :
                            params.value === 'processing' ? 'warning' :
                                params.value === 'cancelled' ? 'error' : 'default'
                    }
                    size="small"
                />
            ),
        },
        {
            field: 'totalAmount',
            headerName: 'Amount',
            flex: 1,
            sortable: true,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (params) => `₹${Number(params.value).toFixed(2)}`,
        },
        {
            field: 'items',
            headerName: 'Tests',
            flex: 1.5,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    alignItems: 'center',
                    maxWidth: '100%',
                    overflow: 'hidden'
                }}>
                    {params.value && Array.isArray(params.value) ? (
                        <>
                            {params.value.slice(0, 2).map((item: LabOrderItem, idx: number) => (
                                <Chip
                                    key={idx}
                                    label={item.testName}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        maxWidth: '120px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                />
                            ))}
                            {params.value.length > 2 && (
                                <Chip
                                    label={`+${params.value.length - 2}`}
                                    size="small"
                                    sx={{ minWidth: 'auto' }}
                                />
                            )}
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary">No tests</Typography>
                    )}
                </Box>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Tooltip title="View Details">
                    <IconButton
                        size="small"
                        onClick={() => handleViewOrder(params.row)}
                        color="primary"
                    >
                        <ViewIcon />
                    </IconButton>
                </Tooltip>
            ),
        },
    ], [handleViewOrder]);

    // Status options for filtering
    const statusOptions = [
        { value: 'all', label: 'All Status' },
        { value: 'pending', label: 'Pending' },
        { value: 'collected', label: 'Collected' },
        { value: 'processing', label: 'Processing' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 1.5, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Lab Test Orders
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchLabOrders}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Company Filter</InputLabel>
                  <Select
                    value={companyFilter}
                    label="Company Filter"
                    onChange={(e) => setCompanyFilter(e.target.value)}
                  >
                    {COMPANY_OPTIONS.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Search Orders"
                  placeholder="Search by patient, order ID, company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Orders Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="lab orders tabs">
              {orders.map((group, index) => (
                <Tab
                  key={group.orderType}
                  label={`${group.orderType} Orders (${group.data.length})`}
                  {...a11yProps(index)}
                  icon={group.orderType === 'Commercial' ? <BusinessIcon /> : <HospitalIcon />}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>

          {orders.map((group, index) => (
            <TabPanel key={group.orderType} value={tabValue} index={index}>
              <MemoizedTabContent
                group={group}
                sortedOrders={sortedOrders}
                loading={loading}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                columns={columns}
              />
            </TabPanel>
          ))}
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Order Details Dialog */}
        <Dialog
            open={detailOpen}
            onClose={() => setDetailOpen(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Order Details</Typography>
                    <IconButton aria-label="close" onClick={() => setDetailOpen(false)}>
                        ✕
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ bgcolor: 'background.paper' }}>
                {selectedOrder ? (
                    <Box>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="overline" color="primary.main">Order</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                                <Chip label={`ID: ${selectedOrder.orderId}`} size="small" />
                                <Chip label={`Date: ${formatDate(selectedOrder.orderDate)}`} size="small" />
                                <Chip label={`Company: ${selectedOrder.companyName}`} size="small" />
                                <Chip
                                    label={selectedOrder.orderType === 'Commercial' ? 'Commercial' : 'Clinic'}
                                    color={selectedOrder.orderType === 'Commercial' ? 'primary' : 'secondary'}
                                    size="small"
                                />
                            </Box>
                        </Box>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="primary.main">Patient</Typography>
                                <Typography variant="body2">{selectedOrder.patientName}</Typography>
                                <Typography variant="body2">Email: {selectedOrder.patientEmail}</Typography>
                                <Typography variant="body2">Phone: {selectedOrder.patientPhone}</Typography>
                                {selectedOrder.doctorName && (
                                    <Typography variant="body2">Doctor: {selectedOrder.doctorName}</Typography>
                                )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="primary.main">Order Info</Typography>
                                <Typography variant="body2">Status: {selectedOrder.status}</Typography>
                                <Typography variant="body2">Total Amount: ₹{selectedOrder.totalAmount}</Typography>
                                <Typography variant="body2">Collection Date: {selectedOrder.collectionDate ? formatDate(selectedOrder.collectionDate) : 'N/A'}</Typography>
                                {selectedOrder.notes && (
                                    <Typography variant="body2">Notes: {selectedOrder.notes}</Typography>
                                )}
                            </Grid>
                        </Grid>

                        <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1 }}>Test Items</Typography>
                        <TableContainer sx={{ maxHeight: 320, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Test Name</TableCell>
                                        <TableCell>Test Code</TableCell>
                                        <TableCell>Category</TableCell>
                                        <TableCell align="right">Price (₹)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selectedOrder.items && selectedOrder.items.length > 0 ? (
                                        selectedOrder.items.map((item : any, index : any) => (
                                            <TableRow key={index} hover>
                                                <TableCell>{item.testName}</TableCell>
                                                <TableCell>{item.testCode || 'N/A'}</TableCell>
                                                <TableCell>{item.category || 'N/A'}</TableCell>
                                                <TableCell align="right">{Number(item.price).toFixed(2)}</TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                                                <NoteAdd />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                                {selectedOrder.items && selectedOrder.items.length > 0 && (
                                    <TableFooter>
                                        <TableRow>
                                            <TableCell colSpan={3} align="right">
                                                <strong>Total:</strong>
                                            </TableCell>
                                            <TableCell align="right">
                                                <strong>₹{selectedOrder.items.reduce((sum: any, item: any) => sum + Number(item.price), 0).toFixed(2)}</strong>
                                            </TableCell>
                                        </TableRow>
                                    </TableFooter>
                                )}
                            </Table>
                        </TableContainer>

                        {selectedOrder.reportUrl && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Typography variant="subtitle2" color="primary.main">Report</Typography>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Visibility />}
                                        href={selectedOrder.reportUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        size="small"
                                    >
                                        View Report
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography>No order selected</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setDetailOpen(false)} variant="outlined" color="primary">Close</Button>
                {selectedOrder && selectedOrder.companyId === '1008' && (
                    <Button
                        onClick={() => {
                            handleGenerateReport(selectedOrder);
                            setDetailOpen(false);
                        }}
                        variant="contained"
                        color="secondary"
                        startIcon={<PictureAsPdf />}
                        size="small"
                    >
                        Generate Report
                    </Button>
                )}
            </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
    );
});

export default LabReportsPage;